import {
  LICENSE_FORMATS,
  PROVINCE_IDS,
  type LicenseFormatConfig,
} from './config';

export interface ValidationResult {
  valid: boolean;
  error?: string;
}

export interface OntarioLicenseInfo {
  dateOfBirth: string;
  gender: 'male' | 'female';
}

type CharType = 'alpha' | 'numeric' | 'alphanumeric' | 'literal';

interface MaskChar {
  type: CharType;
  literal?: string;
}

const parseMaskChar = (char: string): MaskChar => {
  switch (char) {
    case 'A':
      return { type: 'alpha' };
    case '9':
      return { type: 'numeric' };
    case 'X':
      return { type: 'alphanumeric' };
    default:
      return { type: 'literal', literal: char };
  }
};

const filterCharForType = (char: string, type: CharType): string => {
  if (type === 'alpha' && /^[A-Za-z]$/.test(char)) {
    return char.toUpperCase();
  }
  if (type === 'numeric' && /^[0-9]$/.test(char)) {
    return char;
  }
  if (type === 'alphanumeric' && /^[A-Za-z0-9]$/.test(char)) {
    return char.toUpperCase();
  }
  return '';
};

export const formatLicenseNumber = (
  value: string,
  provinceId: number,
): string => {
  const config = LICENSE_FORMATS[provinceId];
  if (!config) {
    return value.toUpperCase();
  }

  const mask = config.mask;
  const maskChars = mask.split('').map(parseMaskChar);

  let result = '';
  let valueIndex = 0;
  const rawValue = value.replace(/[-\s]/g, '');

  for (
    let maskIndex = 0;
    maskIndex < maskChars.length && valueIndex < rawValue.length;
    maskIndex++
  ) {
    const maskChar = maskChars[maskIndex];

    if (maskChar.type === 'literal') {
      result += maskChar.literal;
      continue;
    }

    const inputChar = rawValue[valueIndex];
    const filtered = filterCharForType(inputChar, maskChar.type);

    if (filtered) {
      result += filtered;
      valueIndex++;
    } else {
      valueIndex++;
      maskIndex--;
    }
  }

  if (
    config.maxLength &&
    result.replace(/[-]/g, '').length > config.maxLength
  ) {
    return formatLicenseNumber(result.slice(0, -1), provinceId);
  }

  return result;
};

export const formatLicenseNumberForProvince = (
  value: string,
  provinceId: number,
): string => {
  switch (provinceId) {
    case PROVINCE_IDS.ONTARIO:
      return formatOntarioLicense(value);
    case PROVINCE_IDS.NOVA_SCOTIA:
      return formatNovaScotiaLicense(value);
    case PROVINCE_IDS.ALBERTA:
      return formatAlbertaLicense(value);
    case PROVINCE_IDS.NEW_BRUNSWICK:
      return formatNumericLicense(value, LICENSE_FORMATS[provinceId]);
    default:
      return value.toUpperCase();
  }
};

const formatOntarioLicense = (value: string): string => {
  const raw = value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
  let result = '';

  for (let i = 0; i < raw.length && i < 15; i++) {
    const char = raw[i];

    if (i === 0) {
      if (/^[A-Z]$/.test(char)) {
        result += char;
      }
    } else {
      if (/^[0-9]$/.test(char)) {
        if (result.length === 5 || result.length === 11) {
          result += '-';
        }
        result += char;
      }
    }
  }

  return result;
};

const formatNovaScotiaLicense = (value: string): string => {
  const raw = value.replace(/[^A-Za-z0-9 ]/g, '').toUpperCase();
  let result = '';
  let charIndex = 0;

  for (let i = 0; i < 5 && charIndex < raw.length; i++) {
    const char = raw[charIndex];
    if (/^[A-Z ]$/.test(char)) {
      result += char === ' ' ? ' ' : char;
      charIndex++;
    } else {
      charIndex++;
      i--;
    }
  }

  if (result.length === 5 && charIndex < raw.length) {
    result += '-';
  }

  for (let i = 0; i < 6 && charIndex < raw.length; i++) {
    const char = raw[charIndex];
    if (/^[0-9]$/.test(char)) {
      result += char;
      charIndex++;
    } else {
      charIndex++;
      i--;
    }
  }

  const digitsAfterFirstBlock = result.split('-')[1]?.length || 0;
  if (digitsAfterFirstBlock === 6 && charIndex < raw.length) {
    result += '-';

    for (let i = 0; i < 3 && charIndex < raw.length; i++) {
      const char = raw[charIndex];
      if (/^[0-9]$/.test(char)) {
        result += char;
        charIndex++;
      } else {
        charIndex++;
        i--;
      }
    }
  }

  return result;
};

const formatAlbertaLicense = (value: string): string => {
  const raw = value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
  return raw.slice(0, 9);
};

const formatNumericLicense = (
  value: string,
  config?: LicenseFormatConfig,
): string => {
  const raw = value.replace(/[^0-9]/g, '');
  const maxLen = config?.maxLength || 10;
  return raw.slice(0, maxLen);
};

export const validateLicenseNumber = (
  value: string,
  provinceId: number,
  lastName?: string,
): ValidationResult => {
  if (!value || !value.trim()) {
    return { valid: true };
  }

  const config = LICENSE_FORMATS[provinceId];
  if (!config) {
    return { valid: true };
  }

  const normalized = value.replace(/[-\s]/g, '');
  const length = normalized.length;

  if (length < config.minLength) {
    return {
      valid: false,
      error: `Minimum ${config.minLength} characters required`,
    };
  }

  if (length > config.maxLength) {
    return {
      valid: false,
      error: `Maximum ${config.maxLength} characters allowed`,
    };
  }

  const provinceValidation = validateForProvince(
    normalized,
    provinceId,
    config,
    lastName,
  );
  if (!provinceValidation.valid) {
    return provinceValidation;
  }

  return { valid: true };
};

const validateForProvince = (
  normalized: string,
  provinceId: number,
  config: LicenseFormatConfig,
  lastName?: string,
): ValidationResult => {
  switch (provinceId) {
    case PROVINCE_IDS.ONTARIO:
      if (!/^[A-Z]\d{14}$/.test(normalized)) {
        return {
          valid: false,
          error: `Invalid format. Expected: ${config.description}`,
        };
      }
      if (lastName && lastName.trim()) {
        const licenseFirstLetter = normalized.charAt(0).toUpperCase();
        const lastNameFirstLetter = lastName.trim().charAt(0).toUpperCase();
        if (licenseFirstLetter !== lastNameFirstLetter) {
          return {
            valid: false,
            error: `License first letter (${licenseFirstLetter}) must match last name initial (${lastNameFirstLetter})`,
          };
        }
      }
      break;

    case PROVINCE_IDS.ALBERTA:
      if (!/^[A-Z0-9]{6,9}$/.test(normalized)) {
        return {
          valid: false,
          error: `Invalid format. Expected: ${config.description}`,
        };
      }
      break;

    case PROVINCE_IDS.NOVA_SCOTIA:
      if (!/^[A-Z ]{5}\d{6,9}$/.test(normalized)) {
        return {
          valid: false,
          error: `Invalid format. Expected: ${config.description}`,
        };
      }
      break;

    case PROVINCE_IDS.NEW_BRUNSWICK:
      if (!/^\d+$/.test(normalized)) {
        return {
          valid: false,
          error: `Invalid format. Expected: ${config.description}`,
        };
      }
      break;
  }

  return { valid: true };
};

export const extractOntarioLicenseInfo = (
  licenseNumber: string,
): OntarioLicenseInfo | null => {
  if (!licenseNumber) return null;

  const normalized = licenseNumber.replace(/-/g, '').toUpperCase();
  if (normalized.length !== 15) return null;

  if (!/^[A-Z]\d{14}$/.test(normalized)) return null;

  const birthSegment = normalized.slice(10);

  const yearDigit = parseInt(birthSegment.charAt(0), 10);
  const monthGenderCode = parseInt(birthSegment.slice(1, 3), 10);
  const day = parseInt(birthSegment.slice(3, 5), 10);

  const isFemale = monthGenderCode >= 51 && monthGenderCode <= 62;
  const month = isFemale ? monthGenderCode - 50 : monthGenderCode;

  if (month < 1 || month > 12) return null;
  if (day < 1 || day > 31) return null;

  const currentYear = new Date().getFullYear();
  const currentDecade = Math.floor(currentYear / 10) * 10;

  let fullYear = currentDecade + yearDigit;
  if (fullYear > currentYear) {
    fullYear -= 10;
  }

  while (currentYear - fullYear < 26) {
    fullYear -= 10;
  }

  const dateOfBirth = `${fullYear}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

  return { dateOfBirth, gender: isFemale ? 'female' : 'male' };
};

export const getLicenseFormatInfo = (
  provinceId: number,
): LicenseFormatConfig | null => {
  return LICENSE_FORMATS[provinceId] || null;
};

export const getMaxLengthForProvince = (provinceId: number): number => {
  const config = LICENSE_FORMATS[provinceId];
  if (!config) return 20;

  const hyphenCount = (config.mask.match(/-/g) || []).length;
  return config.maxLength + hyphenCount;
};
